import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { LeftNavLinkNavigationEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { Messages } from '~src/codegen/Localize';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { showDMAwareness } from '~src/commonInfra/components/DMAwareness/DmAwarenessUtils';

import { ILogo, LogoService } from '../../../settings/general-settings/components/sections/siteLogo/services/LogoService';
import { ConnectedSitePicker } from '../../../siteManagement/components/layout/ConnectedSitePicker';
import { SpaceType } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { ContentLinks, ServerLinks, SiteLinks } from './NavLinkDefinitions';
import { NavList, NavListItem, NavListItemRenderProp, NavListItemState, NavListProps } from './NavList';
import { NavLocation, NavLocationAreas } from './NavLocation';
import { NavPanelLogo } from './NavPanelLogo';
import { NavSectionSeparator } from './NavSectionSeparator';

export type NavPanelDisplayMode = 'icon' | 'iconAndText';

export interface NavPanelOwnProps {
  readonly navPanelDisplayMode: NavPanelDisplayMode;
  readonly serverUrls: ServerUrls;
  readonly onSitePickerClick?: () => void;
  readonly onSelectionMade?: () => void;
}

export interface NavPanelStateProps {
  readonly siteUrls: SpacesSiteUrls;
  readonly spaceType: SpaceType;
  readonly currentNavLocation: NavLocation;
  readonly inServerArea: boolean;

  readonly isSessionSiteAdmin: boolean;
  readonly isSessionServerAdmin: boolean;

  readonly isSessionMultiSite: boolean;
  readonly isSiteAdminSettingsEnabled: boolean;
  readonly isAdminViewsEnabled: boolean;

  readonly isCatalogingOnSiteEnabled: boolean;
  readonly isNamedSharingEnabled: boolean;
  readonly isVizRecommendationsEnabled: boolean;
  readonly shouldShowPersonalSpace: boolean;
  readonly isSitePickerEnabled: boolean;
}

export type NavPanelProps = NavPanelOwnProps & NavPanelStateProps;

export const LogoWrapperDivTestId = 'logo-div-wrapper';
export class NavPanel extends React.PureComponent<NavPanelProps> {

  public render(): JSX.Element {
    return (
      <nav data-tb-test-id="nav-panel" aria-label={Messages.navigationPanelTitle()}>
        {this.renderLogo()}
        {this.shouldShowSitePicker() && this.renderSitePicker()}
        {this.navList()}
      </nav>
    );
  }

  private navList(): JSX.Element {
    const items: NavListItem[] = this.shouldShowServerLinks() ? this.serverAdminLinks() : this.siteLinks();
    const props: NavListProps = {
      items: items,
      selectedValue: this.props.currentNavLocation,
      onSelect: this.onNavListSelect
    };

    return <NavList  {...props}/>;
  }

  private onNavListSelect = (value: string) => {
    Telemetry.sendEvent<LeftNavLinkNavigationEvent>({
      type: TelemetryEvents.LeftNavLinkNavigation,
      navLocation: value,
      previousSpace: this.props.spaceType,
    });
    this.onSelection();
  };

  private siteLinks(): NavListItem[] {
      const items = this.getSiteContentLinks();
      if (this.isSessionSiteOrServerAdmin()) {
        return items.concat(this.siteAdminLinks());
      }
      return items;
  }

  private getSiteContentLinks(): NavListItem[] {
    return this.siteContentLinks();
  }

  private siteContentLinks(): NavListItem[] {
    return _.compact<_.List<NavListItem | false>>([
      FeatureFlags.homePage && {
        content: ContentLinks.HomeNavListLink(this.props),
        value: NavLocationAreas.SiteContent.home
      },
      {
        content: ContentLinks.FavoritesNavListLink(this.props),
        value: NavLocationAreas.SiteContent.favorites
      },
      {
        content: ContentLinks.RecentsNavListLink(this.props),
        value: NavLocationAreas.SiteContent.recents
      },
      this.props.isNamedSharingEnabled && {
        content: ContentLinks.SharedWithMeNavListLink(this.props),
        value: NavLocationAreas.SiteContent.shared_with_me
      },
      this.props.isVizRecommendationsEnabled && {
        content: ContentLinks.RecommendationsNavListLink(this.props),
        value: NavLocationAreas.SiteContent.recommendations
      },
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      },
      this.props.shouldShowPersonalSpace && {
        content: ContentLinks.PersonalSpaceNavListLink(this.props),
        value: NavLocationAreas.SiteContent.personal_space
      },
      CollectionUtils.checkCollectionSiteSetting() && {
        content: ContentLinks.CollectionsNavListLink(this.props),
        value: NavLocationAreas.SiteContent.collections
      },
      {
        content: ContentLinks.ExploreNavListLink(this.props),
        value: NavLocationAreas.SiteContent.explore
      },
      FeatureFlags.ntbueGlobalEnabled && {
        content: ContentLinks.PulseNavListLink(this.props),
        value: NavLocationAreas.SiteContent.metrics
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: ContentLinks.CatalogNavListLink(this.props),
        value: NavLocationAreas.SiteContent.catalog
      },
    ]);
  }

  private separatorNavListLink = (navPanelDisplayMode: NavPanelDisplayMode): NavListItemRenderProp => {
    return function(navListItemState: NavListItemState): JSX.Element {
      return <NavSectionSeparator navPanelDisplayMode={navPanelDisplayMode} />;
    };
  };

  private siteAdminLinks(): NavListItem[] {
    const showDataLabelsTab = (this.props.isCatalogingOnSiteEnabled && FeatureFlags.labelManagementEnabled)
      || showDMAwareness(true);
    return _.compact<_.List<NavListItem | false>>([
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      },
      {
        content: SiteLinks.UsersNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_users
      },
      {
        content: SiteLinks.GroupsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_groups
      },
      FeatureFlags.broadcastUI && FeatureFlags.broadcastEnabled && {
        content: SiteLinks.BroadcastsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_broadcasts
      },
      {
        content: SiteLinks.SchedulesNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_schedules
      },
      FeatureFlags.jobManagementEnabled && {
        content: SiteLinks.JobsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_jobs
      },
      {
        content: SiteLinks.TasksNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_tasks
      },
      showDataLabelsTab &&
      {
        content: SiteLinks.LabelsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_labels
      },
      this.statusLinkForSitePages(),
      this.settingLinkForSitePages()
    ]);
  }

  private serverAdminLinks(): NavListItem[] {
    return _.compact<_.List<NavListItem | false>>([
      {
        content: ServerLinks.SitesNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_sites
      },
      {
        content: ServerLinks.UsersNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_users
      },
      {
        content: ServerLinks.SchedulesNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_schedules
      },
      FeatureFlags.jobManagementEnabled && {
        content: ServerLinks.JobsNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_jobs
      },
      {
        content: ServerLinks.TasksNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_tasks
      },
      {
        content: ServerLinks.StatusNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_status
      },
      {
        content: ServerLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_settings
      }
    ]);
  }

  private renderSitePicker() {
    return (
      <ConnectedSitePicker
        navPanelDisplayMode={this.props.navPanelDisplayMode}
        serverUrls={this.props.serverUrls}
        onSitePickerClick={this.props.onSitePickerClick}
        onSelection={this.onSelection}
      />
    );
  }

  private shouldShowServerLinks() {
    return this.props.inServerArea && this.props.isSessionMultiSite;
  }

  private isSessionSiteOrServerAdmin(): boolean {
    return this.props.isSessionSiteAdmin || this.props.isSessionServerAdmin;
  }

  private getCorrectSettingsLinkToShowOnSiteForServerAdmin(): NavListItem {
    if (this.props.isSessionMultiSite) {
      return {
        content: SiteLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_settings
      };
    } else {
      // server admins on a server with only one site, get the server settings page
      return {
        content: ServerLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_settings
      };
    }
  }

  private settingLinkForSitePages(): NavListItem {
    if (this.props.isSessionServerAdmin) {
      return this.getCorrectSettingsLinkToShowOnSiteForServerAdmin();
    } else if (this.props.isSessionSiteAdmin && this.props.isSiteAdminSettingsEnabled) {
      return {
        content: SiteLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_settings
      };
    }

    return undefined;
  }

  private statusLinkForSitePages(): NavListItem {
    if (this.props.isSessionMultiSite || this.props.isSessionSiteAdmin) {
      if (this.props.isAdminViewsEnabled) {
        return {
          content: SiteLinks.StatusNavListLink(this.props),
          value: NavLocationAreas.SiteAdmin.site_status
        };
      } else {
        return undefined;
      }
    } else { // server admin on server with one site
      return {
        content: ServerLinks.StatusNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_status
      };
    }
  }

  private shouldShowSitePicker(): boolean {
    return this.props.isSessionMultiSite && this.props.isSitePickerEnabled;
  }

  private renderLogo(): JSX.Element {
    const logo: ILogo = LogoService.getNavPanelLogo();
    const compactLogo: ILogo = LogoService.getCompactLogo();
    const collapsed = this.props.navPanelDisplayMode === 'icon';

    return <NavPanelLogo logo={logo} compactLogo={compactLogo} siteUrls={this.props.siteUrls} collapsed={collapsed} />;
  }

  private onSelection = () => {
    if (this.props.onSelectionMade) {
      this.props.onSelectionMade();
    }
  };
}
